import { useState, useEffect, type FormEvent } from 'react';
import { useTenantSettings, useUpdateTenantSettings } from '@/hooks/useTenantSettings';
import type { UpdateTenantSettingsInput } from '@/types/tenant-settings';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

function LoadingSkeleton() {
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="h-6 w-40 animate-pulse rounded bg-muted" />
        </CardHeader>
        <CardContent className="space-y-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="space-y-2">
              <div className="h-4 w-24 animate-pulse rounded bg-muted" />
              <div className="h-10 animate-pulse rounded bg-muted" />
            </div>
          ))}
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <div className="h-6 w-32 animate-pulse rounded bg-muted" />
        </CardHeader>
        <CardContent className="space-y-4">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="space-y-2">
              <div className="h-4 w-24 animate-pulse rounded bg-muted" />
              <div className="h-10 animate-pulse rounded bg-muted" />
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

export function SettingsPage() {
  const { data, isLoading, isError, error, refetch } = useTenantSettings();
  const mutation = useUpdateTenantSettings();

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [domain, setDomain] = useState('');
  const [primaryColor, setPrimaryColor] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [logoError, setLogoError] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    if (data) {
      setName(data.name ?? '');
      setSlug(data.slug ?? '');
      setDomain(data.domain ?? '');
      setPrimaryColor(data.settings?.theme_configuration?.primary_color ?? '');
      setLogoUrl(data.settings?.theme_configuration?.logo_url ?? '');
    }
  }, [data]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;

    const payload: UpdateTenantSettingsInput = {};
    if (data) {
      if (name !== data.name) payload.name = name;
      if (slug !== data.slug) payload.slug = slug;
      if ((domain || null) !== data.domain) payload.domain = domain || null;

      const origColor = data.settings?.theme_configuration?.primary_color ?? '';
      const origLogo = data.settings?.theme_configuration?.logo_url ?? '';
      if (primaryColor !== origColor || logoUrl !== origLogo) {
        payload.settings = {
          theme_configuration: {
            ...(primaryColor !== origColor ? { primary_color: primaryColor } : {}),
            ...(logoUrl !== origLogo ? { logo_url: logoUrl } : {}),
          },
        };
      }
    }

    if (Object.keys(payload).length === 0) {
      setSuccessMessage('Keine Änderungen vorhanden.');
      return;
    }

    mutation.mutate(payload, {
      onSuccess: () => {
        setSuccessMessage('Einstellungen erfolgreich gespeichert.');
      },
    });
  }

  return (
    <div>
      <h1 className="text-2xl font-bold">Einstellungen</h1>
      <p className="mt-2 text-muted-foreground">Konfigurieren Sie Ihren Shop.</p>

      <div className="mt-6">
        {isLoading && <LoadingSkeleton />}

        {isError && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-center">
            <p className="text-sm text-red-700">
              Einstellungen konnten nicht geladen werden:{' '}
              {error instanceof Error ? error.message : 'Unbekannter Fehler'}
            </p>
            <Button variant="outline" size="sm" className="mt-3" onClick={() => refetch()}>
              Erneut versuchen
            </Button>
          </div>
        )}

        {data && (
          <form onSubmit={handleSubmit} className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Shop-Informationen</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Shop-Name *</Label>
                  <Input
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    disabled={mutation.isPending}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="slug">Shop-URL (Slug)</Label>
                  <Input
                    id="slug"
                    value={slug}
                    onChange={(e) => setSlug(e.target.value)}
                    disabled={mutation.isPending}
                  />
                  <p className="text-xs text-muted-foreground">
                    Nur Kleinbuchstaben, Zahlen und Bindestriche.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="domain">Shop-Domain (optional)</Label>
                  <Input
                    id="domain"
                    value={domain}
                    onChange={(e) => setDomain(e.target.value)}
                    placeholder="z.B. mein-shop.example.com"
                    disabled={mutation.isPending}
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Darstellung</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="primaryColor">Primärfarbe</Label>
                  <div className="flex items-center gap-3">
                    <Input
                      id="primaryColor"
                      value={primaryColor}
                      onChange={(e) => setPrimaryColor(e.target.value)}
                      placeholder="#3B82F6"
                      disabled={mutation.isPending}
                      className="flex-1"
                    />
                    {/^#[0-9A-Fa-f]{6}$/.test(primaryColor) && (
                      <div
                        className="h-10 w-10 shrink-0 rounded border"
                        style={{ backgroundColor: primaryColor }}
                      />
                    )}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="logoUrl">Logo-URL (optional)</Label>
                  <Input
                    id="logoUrl"
                    value={logoUrl}
                    onChange={(e) => {
                      setLogoUrl(e.target.value);
                      setLogoError(false);
                    }}
                    placeholder="https://example.com/logo.png"
                    disabled={mutation.isPending}
                  />
                  {logoUrl && !logoError && (
                    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
                    <img
                      src={logoUrl}
                      alt="Logo-Vorschau"
                      className="mt-2 h-16 w-auto rounded border object-contain"
                      onError={() => setLogoError(true)}
                    />
                  )}
                  {logoUrl && logoError && (
                    <p className="mt-1 text-xs text-muted-foreground">
                      Logo konnte nicht geladen werden.
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            <div className="flex items-center justify-end gap-4">
              {mutation.isError && (
                <p className="text-sm text-destructive">
                  {mutation.error instanceof Error
                    ? mutation.error.message
                    : 'Speichern fehlgeschlagen.'}
                </p>
              )}
              {successMessage && (
                <p className="text-sm text-green-600">{successMessage}</p>
              )}
              <Button type="submit" disabled={mutation.isPending || !name.trim()}>
                {mutation.isPending ? 'Speichern...' : 'Speichern'}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
